import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import app, { db } from "../../firebase/client";
import { signOut } from "../../auth/firebaseAuth";
import { deviceTimeZone } from "../../utils/time";
import CaregiverBoard from "../../components/CaregiverBoard/CaregiverBoard";
import ManageCaregivers from "../../components/ManageCaregivers/ManageCaregivers";
import BillingBanner, { BillingGate, isBillingBlocked } from "../../components/Billing/BillingBanner";
import TermsGate from "../../components/Terms/TermsGate";
import HelpAssistant from "../../components/HelpAssistant/HelpAssistant";
import PushToggle from "../../components/PushToggle/PushToggle";
import EsperDeviceLink from "../../components/EsperDeviceLink/EsperDeviceLink";
import Sheet from "../../components/Sheet/Sheet";
import EventLog from "../../components/EventLog/EventLog";
import { listenForegroundPush } from "../../utils/push";
import { TERMS_VERSION } from "../Terms/Terms";
import DateDiv from "../../components/date/DateDiv";
import Time from "../../components/time/Time";
import Day from "../../components/day/Day";

const functions = getFunctions(app, "us-central1");
const claimDeviceFn = httpsCallable(functions, "claimDevice");
const reassignDeviceFn = httpsCallable(functions, "reassignDevice");
const deleteResidentFn = httpsCallable(functions, "deleteResident");
const removeDisconnectedScreensFn = httpsCallable(functions, "removeDisconnectedScreens");
const deleteDeviceFn = httpsCallable(functions, "deleteDevice");

// Common timezones for the admin resident-timezone control. The resident's
// current zone + this device's zone are merged in at render so nothing is lost.
const COMMON_TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu",
  "America/Toronto", "Europe/London", "Europe/Paris", "UTC",
];

function Home() {
  const auth = useSelector((state) => state.auth);

  const [residents, setResidents] = useState([]);
  const [devices, setDevices] = useState([]);
  const [moveDeviceId, setMoveDeviceId] = useState("");
  const [delDeviceId, setDelDeviceId] = useState(""); // screen chosen to delete (when >1)
  const [selectedId, setSelectedId] = useState("");
  const [newName, setNewName] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [showCaregivers, setShowCaregivers] = useState(false);
  const [showSettings, setShowSettings] = useState(false); // gear/overflow menu
  const [showResidents, setShowResidents] = useState(false); // mobile: switch/add residents
  const [showAlerts, setShowAlerts] = useState(false);       // mobile: notifications sheet
  const [composeOpen, setComposeOpen] = useState(false);     // mobile: FAB-opened composer
  const [showEventLog, setShowEventLog] = useState(false);   // admin: activity log sheet
  const [tenant, setTenant] = useState(null);
  const [memberDoc, setMemberDoc] = useState(undefined); // undefined=loading
  const [notice, setNotice] = useState(null); // { type: 'ok'|'error', text }

  const tenantId = auth && auth.tenantId;
  const isAdmin = auth && auth.role === "admin";
  const selectedResident = residents.find((r) => r.id === selectedId);
  const residentTz = selectedResident?.timezone || deviceTimeZone();
  const tzOptions = Array.from(new Set([residentTz, deviceTimeZone(), ...COMMON_TIMEZONES]));

  // Set the message font size for the selected resident (applies on both the
  // caregiver board and the resident's display).
  const setMessageSize = async (size) => {
    if (!selectedId) return;
    try {
      await updateDoc(doc(db, "residents", selectedId), { fontSize: size });
    } catch (err) {
      setNotice({ type: "error", text: "Could not update message size." });
    }
  };

  // Toggle the per-resident "new-message chime": the resident's screen plays a
  // soft chime whenever a new message appears (see Display + utils/chime).
  const setChime = async (on) => {
    if (!selectedId) return;
    try {
      await updateDoc(doc(db, "residents", selectedId), { chimeOnNew: on });
    } catch (err) {
      setNotice({ type: "error", text: "Could not update the new-message chime." });
    }
  };

  // Admin sets the resident's timezone — reminders are anchored to it so a
  // family member anywhere means "this time at the facility."
  const setResidentTimezone = async (timezone) => {
    if (!selectedId) return;
    try {
      await updateDoc(doc(db, "residents", selectedId), { timezone });
    } catch (err) {
      setNotice({ type: "error", text: "Could not update timezone." });
    }
  };

  // Admin sets the inactivity threshold: if the board gets no new message/photo
  // for this many days, the whole family circle is nudged (0 = off). Enforced by
  // the daily staleBoardsTick function.
  const setStaleDays = async (v) => {
    if (!selectedId) return;
    const n = Math.max(0, Math.floor(Number(v) || 0));
    try {
      await updateDoc(doc(db, "residents", selectedId), { staleAfterDays: n });
      setNotice({ type: "ok", text: n > 0 ? `The Care Circle will be alerted after ${n} day${n === 1 ? "" : "s"} with no new posts.` : "Inactivity alert off." });
    } catch (err) {
      setNotice({ type: "error", text: "Could not update the inactivity alert." });
    }
  };

  // Daily screen-off (quiet hours): the resident's Display goes dark between
  // quietFrom and quietTo (HH:MM in the resident's timezone). Blank both to disable.
  const setQuietHours = async (from, to) => {
    if (!selectedId) return;
    try {
      await updateDoc(doc(db, "residents", selectedId), { quietFrom: from || null, quietTo: to || null });
      setNotice({ type: "ok", text: (from && to) ? `Screen off ${from}–${to} daily.` : "Quiet hours off." });
    } catch (err) {
      setNotice({ type: "error", text: "Could not update quiet hours." });
    }
  };

  // Toggle this resident's cellular connection (+$12/mo) — the billing trigger
  // adds/removes the cellular line on the subscription.
  const setResidentCellular = async (cellular) => {
    if (!selectedId) return;
    try {
      await updateDoc(doc(db, "residents", selectedId), { cellular });
      setNotice({ type: "ok", text: cellular ? "Cellular added (+$12/mo)." : "Cellular removed." });
    } catch (err) {
      setNotice({ type: "error", text: "Could not update cellular." });
    }
  };

  // Render foreground push notifications (background ones are handled by the
  // FCM service worker). No-op until the user has opted in.
  useEffect(() => { listenForegroundPush(); }, []);

  // Live billing/plan status for the facility (drives the banner + gate).
  useEffect(() => {
    if (!tenantId) { setTenant(null); return; }
    return onSnapshot(doc(db, "tenants", tenantId),
      (s) => setTenant(s.exists() ? { id: s.id, ...s.data() } : null),
      () => {});
  }, [tenantId]);

  // Publish the admin's display name on the tenant doc so family + resident
  // screens can show it in their header (they can't read another user's name).
  // Self-heals: runs whenever the admin views the board.
  useEffect(() => {
    if (!tenant || !isAdmin || !tenantId) return;
    const myName = auth && auth.displayName;
    if (myName && tenant.adminName !== myName) {
      updateDoc(doc(db, "tenants", tenantId), { adminName: myName }).catch(() => {});
    }
  }, [tenant, isAdmin, tenantId, auth]);

  // Live view of my own member doc (Terms acceptance + instant revocation).
  useEffect(() => {
    const uid = auth && auth.uid;
    if (!uid || !tenantId) { setMemberDoc(undefined); return; }
    return onSnapshot(doc(db, "users", uid),
      (s) => setMemberDoc(s.exists() ? s.data() : null),
      () => setMemberDoc(null));
  }, [auth && auth.uid, tenantId]);

  // Instant revocation: if my membership doc is deleted (an admin removed me)
  // while I still hold a tenant claim, sign out immediately rather than waiting
  // for the cached token to expire.
  useEffect(() => {
    if (auth && auth.tenantId && memberDoc === null) signOut();
  }, [auth, memberDoc]);

  // Live, tenant-scoped list of residents. The rules require the tenantId
  // filter, which also drives the composite index we deployed.
  useEffect(() => {
    if (!tenantId) { setResidents([]); return; }
    const q = query(
      collection(db, "residents"),
      where("tenantId", "==", tenantId),
      orderBy("name")
    );
    return onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setResidents(list);
        setSelectedId((cur) => cur || (list[0] && list[0].id) || "");
      },
      () => setNotice({ type: "error", text: "Could not load residents." })
    );
  }, [tenantId]);

  // Live, tenant-scoped list of connected screens — drives the "Move a screen
  // here" control (reassign a display to another resident without a setup code).
  useEffect(() => {
    if (!tenantId) { setDevices([]); return; }
    const q = query(collection(db, "devices"), where("tenantId", "==", tenantId));
    return onSnapshot(
      q,
      (snap) => setDevices(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => { /* non-fatal; the control just won't populate */ }
    );
  }, [tenantId]);

  // Move an already-connected screen (currently on another resident) to the
  // selected resident. The kiosk switches boards live via its device-doc watch.
  const moveScreenHere = async () => {
    if (!selectedId || !moveDeviceId) return;
    try {
      await reassignDeviceFn({ deviceId: moveDeviceId, residentId: selectedId });
      setMoveDeviceId("");
      const name = residents.find((r) => r.id === selectedId)?.name || "resident";
      setNotice({ type: "ok", text: `Screen moved to ${name}.` });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Could not move that screen." });
    }
  };

  const addResident = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const ref = await addDoc(collection(db, "residents"), {
        tenantId,
        name,
        createdBy: auth.uid,
        caregivers: [auth.uid],
        timezone: deviceTimeZone(), // reminders anchor to this; admin can change it
        cellular: false,            // default Wi-Fi; set per resident via the Connection control
        createdAt: serverTimestamp(),
      });
      setNewName("");
      setSelectedId(ref.id);
      setNotice({ type: "ok", text: `Added ${name}.` });
    } catch (err) {
      setNotice({ type: "error", text: "Could not add resident." });
    }
  };

  const claimDevice = async () => {
    const code = pairingCode.trim().toUpperCase();
    if (!code) return;
    if (!selectedId) {
      setNotice({ type: "error", text: "Pick a resident first." });
      return;
    }
    try {
      await claimDeviceFn({ pairingCode: code, residentId: selectedId });
      setPairingCode("");
      const name = residents.find((r) => r.id === selectedId)?.name || "resident";
      setNotice({ type: "ok", text: `Display connected to ${name}.` });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Could not connect that display." });
    }
  };

  const deleteResident = async () => {
    if (!selectedId) {
      setNotice({ type: "error", text: "Pick a resident first." });
      return;
    }
    const name = residents.find((r) => r.id === selectedId)?.name || "this resident";
    if (!window.confirm(`Delete ${name} and their board? This cannot be undone.`)) return;
    try {
      await deleteResidentFn({ residentId: selectedId });
      setSelectedId(""); // the residents subscription will refresh the list
      setShowSettings(false); // close the menu; the resident it referred to is gone
      setNotice({ type: "ok", text: `Deleted ${name}.` });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Could not delete that resident." });
    }
  };

  // Screens in this tenant not connected to any resident (leftover/disconnected).
  const disconnectedCount = devices.filter((d) => !d.residentId).length;
  const removeDisconnectedScreens = async () => {
    if (disconnectedCount === 0) return;
    if (!window.confirm(
      `Remove ${disconnectedCount} disconnected screen${disconnectedCount === 1 ? "" : "s"}? ` +
      `This only removes screens not currently connected to a resident. A real screen ` +
      `re-appears automatically if it's turned on and reconnected.`
    )) return;
    try {
      const { data } = await removeDisconnectedScreensFn();
      setNotice({ type: "ok", text: `Removed ${data.removed} disconnected screen${data.removed === 1 ? "" : "s"}.` });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Could not remove screens." });
    }
  };

  // Screens connected to the selected resident (for the "Delete a screen" control).
  const residentScreens = devices.filter((d) => d.residentId === selectedId);
  // Human label for a screen: its setup code + a battery hint to tell duplicates apart.
  const screenLabel = (d) => {
    const code = d.pairingCode || d.id.slice(0, 6).toUpperCase();
    const batt = typeof d.battery === "number"
      ? ` — ${d.charging ? "⚡" : "🔋"}${Math.round(d.battery)}%`
      : "";
    return `Code ${code}${batt}`;
  };
  // Delete one screen from the resident. If several are connected the admin picks
  // which by its setup code; with exactly one, delete that one directly.
  const deleteScreen = async () => {
    const target = residentScreens.length === 1 ? residentScreens[0].id : delDeviceId;
    if (!target) return;
    const d = residentScreens.find((x) => x.id === target);
    if (!window.confirm(
      `Remove screen ${d ? screenLabel(d) : target} from ${selectedResident?.name || "this resident"}? ` +
      `If it's the live tablet, it will return to its setup code and can be reconnected.`
    )) return;
    try {
      await deleteDeviceFn({ deviceId: target });
      setDelDeviceId("");
      setNotice({ type: "ok", text: "Screen removed." });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Could not remove that screen." });
    }
  };

  // Mobile FAB "+ Message": toggle the composer open/closed; focus it on open.
  const toggleCompose = () => {
    const next = !composeOpen;
    setComposeOpen(next);
    if (next) setTimeout(() => { const el = document.getElementById("mb-input"); if (el) el.focus(); }, 60);
  };

  if (auth === null) {
    return <div className="App"><p style={{ color: "white", padding: "2em" }}>Loading…</p></div>;
  }
  // Logged out (incl. the installed PWA opening at its /home start_url): send to
  // sign-in rather than a dead-end. Sign-in returns the user to /home once in.
  if (auth === false) {
    return <Navigate to="/signin" replace />;
  }
  if (!tenantId) {
    return (
      <div className="App">
        <p style={{ color: "white", padding: "2em" }}>
          Your account isn’t linked to a Care Circle yet. Ask the Care Circle member who set it up to invite you.
        </p>
      </div>
    );
  }

  // Subscription lapsed: block the board (admins can fix it via the portal).
  if (isBillingBlocked(tenant)) {
    return (
      <div className="App">
        <BillingGate tenant={tenant} isAdmin={isAdmin} />
      </div>
    );
  }

  // Member hasn't accepted the current Terms (e.g. a freshly invited caregiver):
  // gate the board until they agree. Only when the member doc EXISTS — a missing
  // doc means removal (handled above by sign-out), not "no terms yet".
  if (memberDoc && memberDoc.termsVersion !== TERMS_VERSION) {
    return (
      <div className="App">
        <TermsGate onAccepted={() => setMemberDoc((d) => ({ ...(d || {}), termsVersion: TERMS_VERSION }))} />
      </div>
    );
  }

  return (
    <div className="App">
      {/* The admin/family-contact name now lives in the top nav (Header), left
          of Log out. */}

      {/* Billing/trial banner is an admin concern; caregivers never see it. */}
      {isAdmin && <BillingBanner tenant={tenant} isAdmin={isAdmin} />}

      <div className="board-datebar">
        <div className="bd-cell bd-date"><DateDiv /></div>
        <div className="bd-cell bd-day"><Day /></div>
        <div className="bd-cell bd-time"><Time /></div>
      </div>

      <div className="manage-bar">
        {/* --- Primary controls: the things an admin reaches for often --- */}
        <div className="manage-group">
          <label>Resident</label>
          <select
            value={selectedId}
            onChange={(e) => { setSelectedId(e.target.value); setNotice(null); }}
            title="Choose which resident's board you're viewing and managing"
            style={{ fontSize: "1.5rem", height: "auto", padding: "0.3rem 0.5rem" }}
          >
            {residents.length === 0 && <option value="">(none yet)</option>}
            {residents.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>

        {isAdmin && selectedId && (
          <div className="manage-group">
            <label>Screen status</label>
            <div className="dev-status">
              <span
                className="dev-badge"
                title={selectedResident?.cellular
                  ? "This resident's screen connects over cellular"
                  : "This resident's screen connects over Wi-Fi"}
              >
                {selectedResident?.cellular ? "📶 Cellular" : "📶 Wi-Fi"}
              </span>
              {(() => {
                const screens = devices.filter((d) => d.residentId === selectedId);
                if (screens.length === 0) {
                  return <span className="dev-badge dev-muted">No screen connected</span>;
                }
                const reported = screens.filter((d) => typeof d.battery === "number");
                return (
                  <>
                    <span
                      className="dev-badge"
                      title={`${screens.length} screen${screens.length === 1 ? "" : "s"} linked to this resident`}
                    >
                      🖥 {screens.length}
                    </span>
                    {reported.map((d) => (
                      <span
                        key={d.id}
                        className="dev-badge"
                        title={`Battery ${Math.round(d.battery)}%${d.charging ? " (charging)" : ""}, reported by the room screen`}
                      >
                        {d.charging ? "⚡" : "🔋"} {Math.round(d.battery)}%
                      </span>
                    ))}
                    {reported.length === 0 && (
                      <span className="dev-badge dev-muted" title="No screen has reported its battery yet">
                        battery: not reported yet
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="manage-group">
            <label>Add resident</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addResident()}
              placeholder="Name"
              title="Create a new resident board (e.g. the person in the room)"
            />
            <button onClick={addResident} title="Add this resident">Add</button>
          </div>
        )}


        {isAdmin && (
          <div className="manage-group">
            <label>Connect a display</label>
            <input
              value={pairingCode}
              onChange={(e) => setPairingCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && claimDevice()}
              placeholder="Setup code"
              maxLength={6}
              title="Enter the 6-character setup code shown on a new screen to link it to this resident"
            />
            <button onClick={claimDevice} title="Link the screen showing this setup code">Connect</button>
          </div>
        )}

        <PushToggle uid={auth.uid} tenantId={tenantId} />

        {/* --- Settings: opens the full settings sheet (mobile-first) --- */}
        {isAdmin && (
          <>
            <div className="manage-group">
              <label>More</label>
              <button
                type="button"
                className="settings-btn"
                onClick={() => setShowSettings(true)}
                title="Resident settings, screen linking, Care Circle, and other options"
              >
                ⚙ Settings
              </button>
            </div>
            <Sheet
              open={showSettings}
              onClose={() => setShowSettings(false)}
              title="Settings"
              subtitle={selectedResident?.name ? `for ${selectedResident.name}` : null}
            >
                  <div className="settings-section-head">
                    {selectedResident?.name ? selectedResident.name : "Resident"}
                  </div>
                  {selectedId && (
                    <div className="manage-group">
                      <label>Screen status</label>
                      <div className="dev-status">
                        <span
                          className="dev-badge"
                          title={selectedResident?.cellular
                            ? "This resident's screen connects over cellular"
                            : "This resident's screen connects over Wi-Fi"}
                        >
                          {selectedResident?.cellular ? "📶 Cellular" : "📶 Wi-Fi"}
                        </span>
                        {(() => {
                          const screens = devices.filter((d) => d.residentId === selectedId);
                          if (screens.length === 0) {
                            return <span className="dev-badge dev-muted">No screen connected</span>;
                          }
                          const reported = screens.filter((d) => typeof d.battery === "number");
                          return (
                            <>
                              <span className="dev-badge" title={`${screens.length} screen${screens.length === 1 ? "" : "s"} linked to this resident`}>
                                🖥 {screens.length}
                              </span>
                              {reported.map((d) => (
                                <span key={d.id} className="dev-badge"
                                  title={`Battery ${Math.round(d.battery)}%${d.charging ? " (charging)" : ""}, reported by the room screen`}>
                                  {d.charging ? "⚡" : "🔋"} {Math.round(d.battery)}%
                                </span>
                              ))}
                              {reported.length === 0 && (
                                <span className="dev-badge dev-muted" title="No screen has reported its battery yet">
                                  battery: not reported yet
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  {selectedId && (
                    <div className="manage-group">
                      <label>Connection</label>
                      <select
                        value={selectedResident?.cellular ? "cellular" : "wifi"}
                        onChange={(e) => setResidentCellular(e.target.value === "cellular")}
                        title="How this resident's screen gets online. Cellular is a paid add-on for rooms without usable Wi-Fi."
                      >
                        <option value="wifi">Wi-Fi (included)</option>
                        <option value="cellular">Cellular (+$12/mo)</option>
                      </select>
                    </div>
                  )}

                  {selectedId && !selectedResident?.cellular && (
                    <EsperDeviceLink
                      key={"esper-" + selectedId}
                      residentId={selectedId}
                      currentEsperId={selectedResident?.esperDeviceId}
                      onNotice={({ ok, msg }) => setNotice({ type: ok ? "ok" : "error", text: msg })}
                    />
                  )}

                  {selectedId && devices.some((d) => d.residentId && d.residentId !== selectedId) && (
                    <div className="manage-group">
                      <label>Move a screen here</label>
                      <select value={moveDeviceId} onChange={(e) => setMoveDeviceId(e.target.value)}
                        title="Move a screen already connected to another resident over to this one (no setup code needed)">
                        <option value="">Choose a screen…</option>
                        {devices
                          .filter((d) => d.residentId && d.residentId !== selectedId)
                          .map((d) => {
                            const from = residents.find((r) => r.id === d.residentId)?.name || "another resident";
                            return <option key={d.id} value={d.id}>From {from}</option>;
                          })}
                      </select>
                      <button onClick={moveScreenHere} disabled={!moveDeviceId} title="Move the chosen screen to this resident">Move</button>
                    </div>
                  )}

                  {selectedId && residentScreens.length > 0 && (
                    <div className="manage-group">
                      <label>Delete a screen</label>
                      <div className="screen-del-controls">
                        {residentScreens.length > 1 ? (
                          <select value={delDeviceId} onChange={(e) => setDelDeviceId(e.target.value)}
                            title="Choose which screen to remove from this resident (identified by its setup code)">
                            <option value="">Choose a screen…</option>
                            {residentScreens.map((d) => (
                              <option key={d.id} value={d.id}>{screenLabel(d)}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="screen-del-code" title="This screen's setup code">
                            {screenLabel(residentScreens[0])}
                          </span>
                        )}
                        <button
                          className="btn-danger"
                          onClick={deleteScreen}
                          disabled={residentScreens.length > 1 && !delDeviceId}
                          title="Remove this screen record from the resident. A live tablet returns to its setup code and can be reconnected.">
                          Delete
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="manage-group">
                    <label>Message size</label>
                    <select
                      value={selectedResident?.fontSize || "small"}
                      onChange={(e) => setMessageSize(e.target.value)}
                      disabled={!selectedId}
                      title="How large messages appear on the resident's screen"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </select>
                  </div>

                  {selectedId && (
                    <div className="manage-group">
                      <label>New-message chime</label>
                      <select
                        value={selectedResident?.chimeOnNew ? "on" : "off"}
                        onChange={(e) => setChime(e.target.value === "on")}
                        title="Play a soft chime on the resident's screen whenever a new message appears"
                      >
                        <option value="off">Off</option>
                        <option value="on">On</option>
                      </select>
                    </div>
                  )}

                  <div className="manage-group">
                    <label>Resident timezone</label>
                    <select
                      value={residentTz}
                      onChange={(e) => setResidentTimezone(e.target.value)}
                      disabled={!selectedId}
                      title="The resident's local timezone — reminder times are shown in it"
                    >
                      {tzOptions.map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                  </div>

                  {selectedId && (
                    <div className="manage-group">
                      <label>Inactivity alert (days)</label>
                      <input
                        key={selectedId}
                        type="number"
                        min="0"
                        defaultValue={selectedResident?.staleAfterDays ?? 0}
                        onBlur={(e) => setStaleDays(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                        title="Nudge the whole Care Circle if no new message or photo has been posted for this many days (0 = off)"
                        style={{ width: "5em" }}
                      />
                    </div>
                  )}

                  {selectedId && (
                    <div className="manage-group">
                      <label>Quiet hours (screen off)</label>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
                        <input
                          key={"qf-" + selectedId}
                          type="time"
                          defaultValue={selectedResident?.quietFrom || ""}
                          onBlur={(e) => setQuietHours(e.target.value, selectedResident?.quietTo || "")}
                          title="Screen goes dark starting at this time (resident's timezone)"
                          style={{ width: "7em" }}
                        />
                        <span style={{ color: "var(--d-muted)" }}>to</span>
                        <input
                          key={"qt-" + selectedId}
                          type="time"
                          defaultValue={selectedResident?.quietTo || ""}
                          onBlur={(e) => setQuietHours(selectedResident?.quietFrom || "", e.target.value)}
                          title="Screen comes back on at this time (resident's timezone)"
                          style={{ width: "7em" }}
                        />
                        <button
                          onClick={() => setQuietHours("", "")}
                          title="Turn quiet hours off (screen always on)"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Off
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedId && (
                    <div className="manage-group">
                      <label>Activity log</label>
                      <button
                        onClick={() => { setShowSettings(false); setShowEventLog(true); }}
                        title="A metadata-only history of posts, edits, deletes, and alarms for this resident (no message text or photos)">
                        View activity
                      </button>
                    </div>
                  )}

                  {selectedId && (
                    <div className="manage-group settings-menu-danger">
                      <label>Delete resident</label>
                      <button
                        className="btn-danger"
                        onClick={deleteResident}
                        title="Permanently delete this resident, their messages, photos, and unlink their screen (asks to confirm)"
                      >
                        Delete {selectedResident?.name || "resident"}
                      </button>
                    </div>
                  )}

                  <div className="settings-section-head settings-section-gap">
                    {tenant?.name ? `Care circle: ${tenant.name}` : "Care circle"}
                  </div>

                  <div className="manage-group">
                    <label>Care Circle</label>
                    <button onClick={() => { setShowSettings(false); setShowCaregivers(true); }}
                      title="Invite Care Circle members, see who's in this Care Circle, or remove someone">
                      Manage Care Circle
                    </button>
                  </div>

                  <div className="manage-group">
                    <label>Screens</label>
                    <button
                      onClick={removeDisconnectedScreens}
                      disabled={disconnectedCount === 0}
                      title="Remove leftover screen records not currently connected to a resident (e.g. from testing). Connected screens are left alone.">
                      {disconnectedCount === 0
                        ? "No disconnected screens"
                        : `Remove ${disconnectedCount} disconnected screen${disconnectedCount === 1 ? "" : "s"}`}
                    </button>
                  </div>

                  <div className="manage-group">
                    <label>Legal</label>
                    <button
                      onClick={() => window.open("/terms", "_blank", "noopener")}
                      title="Open the Terms of Service in a new tab">
                      Terms &amp; Conditions
                    </button>
                  </div>
            </Sheet>
          </>
        )}

      </div>

      {notice && (
        <div className={notice.type === "ok" ? "manage-notice ok" : "manage-notice error"}>
          {notice.text}
        </div>
      )}

      <div className="mobile-context">
        {selectedResident?.name ? `${selectedResident.name}'s board` : "Select a resident"}
      </div>

      <div className="home-board-wrap">
        <CaregiverBoard
          boardId={selectedId}
          tenantId={tenantId}
          authorUid={auth.uid}
          authorName={auth.displayName || (auth.email ? auth.email.split("@")[0] : "")}
          fontSize={selectedResident?.fontSize || "small"}
          residentTimezone={residentTz}
          cellular={!!selectedResident?.cellular}
          composeOpen={composeOpen}
          onComposeClose={() => setComposeOpen(false)}
          staleAfterDays={selectedResident?.staleAfterDays || 0}
          residentCreatedAt={selectedResident?.createdAt?.toMillis?.() || 0}
          residentName={selectedResident?.name}
        />
      </div>

      {auth.role === "admin" && (
        <ManageCaregivers
          open={showCaregivers}
          onClose={() => setShowCaregivers(false)}
          tenantId={tenantId}
          myUid={auth.uid}
        />
      )}

      {/* Mobile bottom action bar (shown only on phone widths via CSS). */}
      <nav className="mobile-tabbar">
        <button className="mtab" onClick={() => setShowResidents(true)} title="Switch or add residents">
          <span className="mtab-ic">👤</span><span className="mtab-lbl">Resident</span>
        </button>
        <button className={"mtab mtab-primary" + (composeOpen ? " mtab-on" : "")}
          onClick={toggleCompose} title={composeOpen ? "Close the composer" : "Write a new message"}>
          <span className="mtab-ic">{composeOpen ? "✕" : "＋"}</span>
          <span className="mtab-lbl">{composeOpen ? "Close" : "Message"}</span>
        </button>
        <button className="mtab" onClick={() => setShowAlerts(true)} title="Notifications">
          <span className="mtab-ic">🔔</span><span className="mtab-lbl">Alerts</span>
        </button>
        {isAdmin && (
          <button className="mtab" onClick={() => setShowSettings(true)} title="Settings & Care Circle">
            <span className="mtab-ic">⚙</span><span className="mtab-lbl">Settings</span>
          </button>
        )}
      </nav>

      {/* Residents sheet: switch resident, and (admin) add one / connect a display. */}
      <Sheet
        open={showResidents}
        onClose={() => setShowResidents(false)}
        title="Residents"
        subtitle={selectedResident?.name ? `Current: ${selectedResident.name}` : null}
      >
        <div className="settings-section-head">Switch resident</div>
        <div className="res-switch-list">
          {residents.length === 0 && <div className="res-empty">No residents yet.</div>}
          {residents.map((r) => (
            <button
              key={r.id}
              className={"res-switch-item" + (r.id === selectedId ? " on" : "")}
              onClick={() => { setSelectedId(r.id); setNotice(null); setShowResidents(false); }}
            >
              {r.name}{r.id === selectedId ? "  ✓" : ""}
            </button>
          ))}
        </div>
        {isAdmin && (
          <>
            <div className="settings-section-head settings-section-gap">Add a resident</div>
            <div className="manage-group">
              <label>Name</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addResident()}
                placeholder="Name"
              />
              <button onClick={addResident}>Add</button>
            </div>
            <div className="settings-section-head settings-section-gap">Connect a display</div>
            <div className="manage-group">
              <label>Setup code</label>
              <input
                value={pairingCode}
                onChange={(e) => setPairingCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && claimDevice()}
                placeholder="Setup code"
                maxLength={6}
              />
              <button onClick={claimDevice}>Connect</button>
            </div>
          </>
        )}
      </Sheet>

      {/* Activity log sheet (admin-only, metadata-only). */}
      <EventLog
        open={showEventLog}
        onClose={() => setShowEventLog(false)}
        residentId={selectedId}
        tenantId={tenantId}
        residentName={selectedResident?.name}
      />

      {/* Alerts sheet: this device's push notifications. */}
      <Sheet open={showAlerts} onClose={() => setShowAlerts(false)} title="Notifications">
        <p className="sheet-note">
          Get a push notification on this device when a new message or photo is posted.
        </p>
        <div className="alerts-toggle"><PushToggle uid={auth.uid} tenantId={tenantId} /></div>
      </Sheet>

      <HelpAssistant />
    </div>
  );
}

export default Home;
